import type {
  AuthType,
  BodyMode,
  HttpMethod,
  RawFormat,
  RequestHeader,
  RequestVariable,
} from '@core/entities/ApiRequest';
import type { RequestSettings } from '@core/entities/RequestSettings';
import type { ApiRequestDocumentPayload } from '@core/repositories/ApiRequestRepository';

/**
 * The document-shaped subset both `DraftRequest` (an open tab's in-memory
 * edits) and the full `ApiRequest` entity (a request fetched for e.g. the
 * Collection Runner) satisfy structurally — letting one payload builder and
 * one variable-merge helper serve both call sites instead of drifting apart
 * as two near-identical copies.
 */
export interface DocumentSource {
  name: string;
  method: HttpMethod;
  url: string;
  description: string | null;
  authType: AuthType;
  authConfig: Record<string, unknown> | null;
  preRequestScript: string | null;
  testScript: string | null;
  settings: RequestSettings;
  params: {
    type: 'query' | 'path';
    key: string;
    value: string | null;
    description: string | null;
    isEnabled: boolean;
  }[];
  headers: RequestHeader[];
  variables: RequestVariable[];
  body: {
    mode: BodyMode;
    rawFormat: RawFormat | null;
    rawContent: string | null;
    graphqlQuery: string | null;
    graphqlVariables: Record<string, unknown> | null;
    binaryFilePath: string | null;
    fields: {
      key: string;
      value: string | null;
      type: 'text' | 'file';
      filePath: string | null;
      isEnabled: boolean;
    }[];
  } | null;
}

export const buildDocumentPayload = (source: DocumentSource): ApiRequestDocumentPayload => ({
  name: source.name,
  method: source.method,
  url: source.url,
  description: source.description,
  authType: source.authType,
  authConfig: source.authConfig,
  preRequestScript: source.preRequestScript,
  testScript: source.testScript,
  settings: source.settings,
  params: source.params.map((param) => ({
    type: param.type,
    key: param.key,
    value: param.value,
    description: param.description,
    isEnabled: param.isEnabled,
  })),
  headers: source.headers.map((header) => ({
    key: header.key,
    value: header.value,
    description: header.description,
    isEnabled: header.isEnabled,
  })),
  variables: source.variables.map((variable) => ({
    key: variable.key,
    value: variable.value,
    type: variable.type,
    isEnabled: variable.isEnabled,
  })),
  body: source.body
    ? {
        mode: source.body.mode,
        rawFormat: source.body.rawFormat,
        rawContent: source.body.rawContent,
        graphqlQuery: source.body.graphqlQuery,
        graphqlVariables: source.body.graphqlVariables,
        binaryFilePath: source.body.binaryFilePath,
        fields: source.body.fields.map((field) => ({
          key: field.key,
          value: field.value,
          type: field.type,
          filePath: field.filePath,
          isEnabled: field.isEnabled,
        })),
      }
    : null,
});

export const buildHeaderMap = (headers: RequestHeader[]): Record<string, string> =>
  Object.fromEntries(
    headers.filter((header) => header.isEnabled).map((header) => [header.key, header.value ?? '']),
  );

export const buildVariableMap = (variables: RequestVariable[]): Record<string, string | null> =>
  Object.fromEntries(
    variables
      .filter((variable) => variable.isEnabled)
      .map((variable) => [variable.key, variable.value]),
  );

export const flattenResponseHeaders = (
  headers: Record<string, string[]> | null | undefined,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(headers ?? {}).map(([key, values]) => [key, values.join(', ')]),
  );

/** Layers pre-request-script variable changes on top of the request's saved variables, for this send only. */
export const mergeVariableChanges = (
  payload: ApiRequestDocumentPayload,
  changes: Record<string, string>,
): ApiRequestDocumentPayload => {
  if (Object.keys(changes).length === 0) return payload;

  const existingKeys = new Set((payload.variables ?? []).map((variable) => variable.key));
  const updated = (payload.variables ?? []).map((variable) =>
    variable.key in changes ? { ...variable, value: changes[variable.key] } : variable,
  );
  const added = Object.entries(changes)
    .filter(([key]) => !existingKeys.has(key))
    .map(([key, value]) => ({ key, value, type: 'default' as const, isEnabled: true }));

  return { ...payload, variables: [...updated, ...added] };
};
